<template>
  <div v-if="show"
    class="fixed inset-0 z-[10003] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
    <div
      class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
      <div class="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
        <h3 class="text-xl font-bold text-gray-800">Generated Barcodes</h3>
        <button @click="handleClose" class="p-2 hover:bg-gray-200 rounded-full transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24"
            stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div class="p-8 overflow-y-auto bg-gray-100">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex items-center justify-center py-12">
          <div class="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        </div>
        <!-- Barcodes -->
        <div v-else-if="displayBarcodes.length > 0" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 print:grid-cols-4" id="barcode-print-area">
          <template v-for="barcodeItem in displayBarcodes" :key="barcodeItem.barcode_string">
            <div v-for="n in barcodeItem.quantity" :key="`${barcodeItem.barcode_string}-${n}`"
              class="barcode-label bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col items-center gap-2 break-inside-avoid print:border-0 print:shadow-none print:p-2">
              <!-- Barcode -->
              <div v-if="barcodeItem.barcode_string" class="w-full flex justify-center print-barcode">
                <vue-barcode :value="barcodeItem.ascii_barcode_string" :options="{
                  format: 'CODE128',
                  width: 2,
                  height: 50,
                  displayValue: true,
                  fontSize: 40,
                  margin: 5,
                  background: '#ffffff',
                  lineColor: '#000000',
                }" tag="svg" class="max-w-full print:max-w-none" />
              </div>
            </div>
          </template>
        </div>
        <!-- Empty State -->
        <div v-else class="flex items-center justify-center py-12 text-gray-500">
          <p>No barcodes available</p>
        </div>
      </div>

      <div class="p-6 border-t border-gray-100 bg-white flex justify-end gap-3">
        <button @click="handleClose"
          class="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors">
          Close
        </button>
        <button @click="printBarcodes" :disabled="isLoading || displayBarcodes.length === 0"
          class="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
            stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          Print Barcodes
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, ref, computed, watch } from 'vue';
import VueBarcode from '@chenfengyuan/vue-barcode';
import { showToast } from '@/composables/swal';
import { OrderForBarcode, ApiResponseV1 } from '@/models';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { AxiosError } from 'axios';
import { handleResponseException } from '@/interceptors/axiosRequestInterceptor';

const props = defineProps<{
  show: boolean;
  barcodes?: OrderForBarcode['barcode_data'];
  order_no?: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const isLoading = ref(false);
const fetchedBarcodes = ref<OrderForBarcode['barcode_data']>([]);

// Computed property to get the barcodes to display
const displayBarcodes = computed(() => {
  // If barcodes prop is provided, use it
  if (props.barcodes && props.barcodes.length > 0) {
    return props.barcodes;
  }
  // Otherwise use fetched barcodes
  return fetchedBarcodes.value;
});

const fetchBarcodeData = async (orderNo: string) => {
  if (!orderNo.trim()) {
    return;
  }

  isLoading.value = true;
  fetchedBarcodes.value = [];

  try {
    const response = await http.post<ApiResponseV1<OrderForBarcode>>(`order/${orderNo}/for/barcode`);
    if (response.data.result && response.data.result.barcode_data) {
      fetchedBarcodes.value = response.data.result.barcode_data;
      showToast(response.data.message || 'Barcode data loaded successfully', 'success');
    } else {
      showToast('No barcode data found for this order', 'error');
    }
  } catch (error) {
    const axiosError = error as AxiosError<ApiResponseV1<null>>;
    showToast(axiosError.response?.data.message || 'Failed to fetch barcode data', 'error');
    handleResponseException(axiosError);
  } finally {
    isLoading.value = false;
  }
};

// Watch for show prop and order_no to fetch data when modal opens
watch([() => props.show, () => props.order_no], ([show, orderNo]) => {
  if (show && orderNo && !props.barcodes) {
    fetchBarcodeData(orderNo);
  } else if (show && props.barcodes) {
    // If barcodes are provided via prop, clear fetched data
    fetchedBarcodes.value = [];
  }
}, { immediate: true });



const handleClose = () => {
  emit('close');
};

const printBarcodes = () => {
  const barcodeArea = document.getElementById('barcode-print-area');
  if (!barcodeArea) {
    showToast('Barcode area not found', 'error');
    return;
  }

  // Clone the barcode area
  const printContent = barcodeArea.cloneNode(true);

  // Create a temporary container
  const tempContainer = document.createElement('div');
  tempContainer.id = 'print-container';
  tempContainer.style.cssText = `
    position: absolute;
    left: -9999px;
    top: -9999px;
    width: 100%;
  `;
  tempContainer.appendChild(printContent);
  document.body.appendChild(tempContainer);

  // Wait a bit for DOM to update, then print
  setTimeout(() => {
    window.print();
    // Clean up after print dialog closes
    setTimeout(() => {
      document.body.removeChild(tempContainer);
    }, 1000);
  }, 100);
};
</script>

<style>
@media print {
  @page {
    size: 50.8mm 38.1mm; /* 2" x 1.5" */
    margin: 0;
    padding: 0;
  }

  body {
    margin: 0;
    padding: 0;
  }

  body > * {
    display: none !important;
  }

  /* Only print container */
  #print-container {
    display: block !important;
    position: static !important;
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  #barcode-print-area {
    display: grid !important;
    grid-template-columns: 1fr !important; /* One barcode per page */
    gap: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .barcode-label {
    width: 50.8mm !important;   /* 2.5 inch */
    height: 38.1mm !important;  /* 1.5 inch */
    padding: 2mm !important;
    margin: 0 !important;
    page-break-after: always !important;
    background: #fff !important;
    border: 0 !important;
    box-shadow: none !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .print-barcode {
    width: 100% !important;
    height: 100% !important;
    margin: 0;
    padding: 0;
    display: flex !important;
    justify-content: center;
    align-items: center;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

/* Screen styles */
.barcode-label {
  transition: transform 0.2s;
}

.barcode-label:hover {
  transform: scale(1.02);
}
</style>

